import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import * as authApi from '../api/auth';
import { authKeys, type AuthUser } from '../api/auth';

async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    return await authApi.getCurrentUser();
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      return null;
    }
    throw error;
  }
}

export function useAuth() {
  const queryClient = useQueryClient();

  const { data: user = null, isPending: isLoading } = useQuery({
    queryKey: authKeys.me,
    queryFn: fetchCurrentUser,
  });

  const loginMutation = useMutation({
    mutationFn: ({
      username,
      password,
    }: {
      username: string;
      password: string;
    }) => authApi.login(username, password),
    onSuccess: (nextUser) => {
      queryClient.setQueryData(authKeys.me, nextUser);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      queryClient.setQueryData(authKeys.me, null);
    },
  });

  const login = useCallback(
    async (username: string, password: string) => {
      await loginMutation.mutateAsync({ username, password });
    },
    [loginMutation]
  );

  const logout = useCallback(async () => {
    window.dispatchEvent(new CustomEvent('logout'));
    await new Promise((r) => setTimeout(r, 100));
    try {
      await logoutMutation.mutateAsync();
    } catch {
      // session cleared locally in onSettled
    }
  }, [logoutMutation]);

  return {
    user,
    isLoading,
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    login,
    logout,
  };
}
